import BpkText, {
  TEXT_STYLES,
} from '@skyscanner/backpack-web/bpk-component-text';
import { cssModules } from '@skyscanner/backpack-web/bpk-react-utils';

import { withI18n } from '../../../contexts/i18n';
import PriceCugDiscount from '../PriceCugDiscount';
import SavingsItem from '../SavingsItem';
import SelectedNightsLabel from '../SelectedNightsLabel';
import TotalPriceSubtitle from '../TotalPriceSubtitle';

import type {
  PriceBreakdownInfo,
  PriceBreakdownInfoProps,
} from '@shared/types/frontend/price-breakdown';
import type { I18nService } from '@shared/types/i18n';
import type { UserContext } from '@shared/types/user-context';

import STYLES from './CostItems.module.scss';

import { withUserContext } from '@shared/utils-frontend/contexts/UserContext';
import { isTaxesFeesIncludedPolicy } from '@shared/utils-frontend/price-policy';

type Props = {
  i18n: I18nService;
  costInfo: PriceBreakdownInfo | PriceBreakdownInfoProps;
  numberOfNights: number;
  userContext: UserContext;
};

const cls = cssModules(STYLES);

const CostItems = ({
  costInfo,
  i18n,
  numberOfNights,
  userContext: { flightsCugEnabled: cugEnabled },
}: Props) => {
  const {
    isPerNightType,
    priceInfo: {
      discountInfo,
      originalBasicPricePerNight,
      originalBasicPriceTotal,
      price,
      priceWithAllTaxes,
      secondaryPrice,
      taxesAndFees,
    },
    taxAndFeeInclusionPolicy,
  } = costInfo;
  const taxesIncluded = isTaxesFeesIncludedPolicy(taxAndFeeInclusionPolicy);
  let cug;
  const priceItemClassName = cls('CostItems__priceItem');

  if (discountInfo && discountInfo.priceWithoutDiscount && cugEnabled) {
    const { discountAmount, discountType, percentage, priceWithoutDiscount } =
      discountInfo;
    cug = {
      type: discountType,
      originalPrice: priceWithoutDiscount,
      discountPercentage: percentage,
      discountPrice: discountAmount,
    };
  }

  return (
    <>
      <div className={priceItemClassName}>
        <SelectedNightsLabel
          numberOfNights={numberOfNights}
          originalBasicPricePerNight={originalBasicPricePerNight}
        />
        <BpkText textStyle={TEXT_STYLES.label1}>
          {i18n.formatCurrencyFull(originalBasicPriceTotal)}
        </BpkText>
      </div>
      <div
        className={cls(
          'CostItems__taxesAndDiscount',
          !taxesIncluded && 'CostItems__taxesAndDiscount--reverse',
        )}
      >
        <div>
          {taxesAndFees?.taxTypes.map((tax) => (
            <div key={tax.type} className={priceItemClassName}>
              <div>
                <BpkText textStyle={TEXT_STYLES.label1}>{tax.title}</BpkText>
              </div>
              <BpkText textStyle={TEXT_STYLES.label1}>
                {i18n.formatCurrencyFull(tax.price)}
              </BpkText>
            </div>
          ))}
        </div>
        {cug ? (
          <div className={priceItemClassName}>
            <SavingsItem cug={cug} />
            {cug.discountPrice && (
              <PriceCugDiscount discountAmount={cug.discountPrice} />
            )}
          </div>
        ) : null}
      </div>

      <div className={cls('CostItems__priceTotal')}>
        <div className={cls('CostItems__priceStatistics')}>
          <BpkText
            textStyle={TEXT_STYLES.heading4}
            className={cls('CostItems__priceStatisticsTitle')}
          >
            {i18n.translate('SelectionSummary_label_total')}
          </BpkText>
          <BpkText textStyle={TEXT_STYLES.heading4}>
            {i18n.formatCurrencyFull(priceWithAllTaxes)}
          </BpkText>
        </div>
      </div>

      {(numberOfNights > 1 || !taxesIncluded) && (
        <TotalPriceSubtitle
          pricePerNight={isPerNightType ? price : secondaryPrice}
          taxAndFeeInclusionPolicy={taxAndFeeInclusionPolicy}
        />
      )}
    </>
  );
};

export default withUserContext(withI18n(CostItems));
